import styled from '@emotion/styled'
import { Upload } from 'antd'
import { RcFile, UploadChangeParam } from 'antd/lib/upload'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import color from '../../constant/colors'
import { useVisibility } from '../../utils/custom-hook'
import { mobile, useScreen } from '../../utils/responsive-helper'
import Button from './Button'
import Image, { ImageNameKeys } from './Image'
import Modal from './Modal'
import OutlineButton from './OutlineButton'
import Text from './Text'

const Layout = styled.div`
  background-color: WHITE;
  width: 100%;
  height: 100%;
  border-radius: 8px;
  border: solid 1px ${color.DIM_GRAY};
`

const ImagePreviewPanel = styled.div<{ isMobile: boolean }>`
  padding: 1px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  width: 100%;
  height: 100%;
  column-gap: 12px;
`

const CommentImage = styled.img`
  max-width: 50%;
  border: 0px solid black;
  object-fit: contain;   

  ${mobile} {
    object-fit: cover;
  }
`

const LayoutRowButtonUpload = styled.div`

  ${mobile} {
    padding-left: 0px;
    padding-right: 0px;
    span {
      width: 100%;
    }
  }

  .ant-upload {
    ${mobile} {
      width: 100%;
    }
  }
`

const LayoutModal = styled.div`
  margin-top: 50px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`

const LayoutButtonModal = styled(Button)`
  width: 300px;
  margin: 50px 0 0 0;
`
const LayoutBodyColumn = styled.div`
  padding: 8px;

  ${mobile} {
    padding: 12px;
  }
`

const LayoutBodyRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding-bottom: 16px;
`

const LayoutTitleRow = styled.div`
  flex: 1;
  display: 'flex';
  flex-direction: 'column';
`
const LayoutBackgroundFooter = styled.div`
  padding: 8px;
`
const BackgroundUpload = styled.div`
  background: #ECECEC;
  height: 150px;
  width: 100%;
  border-radius: 8px;
`

export type UploadFileProps = {
  icon?: ImageNameKeys
  title: string
  desc: string
  value?: string
  onChange?: (v: any) => void
  isDelete?: boolean
  disabled?: boolean
  /**
   * maximumFileSize in MB, default 2 MB
   * */
  maximumFileSize?: number
  isPreview?: boolean
}

const UploadFile = (props: UploadFileProps) => {
  const {
    icon = null,
    title,
    desc,
    onChange,
    value,
    isDelete = false,
    maximumFileSize = 3,
    disabled = false,
  } = props
  const { t } = useTranslation()
  const [selectedFile, setSelectedFile] = useState<any>(value || '')
  const [selectedFileName, setSelectedFileName] = useState('')
  const [preview, setPreview] = useState('')
  const { isMobile } = useScreen()
  const [modalVisible, modalAction] = useVisibility()
  const [errorMessage, setErrorMessage] = useState('')
  const [isFile, setIsFile] = useState(false)


  useEffect(() => {

    if (!selectedFile) {
      setPreview('')
      return
    }

    setPreview(selectedFile)

    // if (!selectedFile.includes('data:image') && selectedFileName === '' && selectedFile.substr(0, 8) !== 'https://') {
    //   setSelectedFileName(`file: ${selectedFile.split('/')[1].split(';')[0]}`)
    // }

    if (!selectedFile.includes('data:image'))
      setIsFile(true)
    else
      setIsFile(false)

  }, [selectedFile, selectedFileName])

  const onRemoveClick = useCallback(
    (e) => {
      if (!disabled) {
        e.preventDefault()
        setPreview('')
        setSelectedFile('')
        onChange?.('')
      }
    },
    [disabled, onChange],
  )

  const getBase64 = useCallback(async (file: any) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => resolve(reader.result)
      reader.onerror = (error) => reject(error)
    })
  }, [])

  const onSelectedFile = useCallback(
    async (params: UploadChangeParam) => {
      const { file: image } = params
      const isLarge = image.size / 1024 / 1024 > maximumFileSize

      if (!isLarge) {
        if (image) {
          const imageBase64 = (await getBase64(image)) as string
          onChange?.(imageBase64)
          setSelectedFile(imageBase64)

          if (!image.type.includes('image')) {
            setSelectedFileName(image?.name)
          }
        }
      }
    },
    [getBase64, onChange, maximumFileSize],
  )

  const validateUploadFile = useCallback(
    (image: RcFile) => {
      const isLarge = image.size / 1024 / 1024 > maximumFileSize
      if (isLarge) {
        modalAction.show()
        setPreview('')
        setSelectedFile('')
        onChange?.('')
        setErrorMessage('ขนาด ภาพ/ไฟล์ ใหญ่เกินไป')
      }
    },
    [maximumFileSize, modalAction, onChange],
  )

  const beforeUpload = useCallback(
    (image: RcFile) => {
      validateUploadFile(image)
      return false
    },
    [validateUploadFile],
  )

  return (
    <Layout className="layout-upload">
      <Modal visible={modalVisible} onCancel={modalAction.hide} hideFooter>
        <LayoutModal>
          <Text>{errorMessage}</Text>
          <LayoutButtonModal onClick={modalAction.hide}>Cancel</LayoutButtonModal>
        </LayoutModal>
      </Modal>
      <LayoutBodyColumn>
        <LayoutBodyRow>
          {/* <Image imageName={icon} width={48} height={48} /> */}
          <LayoutTitleRow>
            <Text size={18} type={'Medium'}>
              {title}
            </Text>
            <Text size={14} type={'Medium'}>
              {desc}
            </Text>
            <Text size={14} type={'Medium'} color={color.FONT_LIGHT_GRAY}>
              (jpg, png, pdf, zip ขนาดไม่เกิน 2 MB)
            </Text>
          </LayoutTitleRow>
          {!selectedFile && (
            <Upload
              action=""
              listType="text"
              accept=".jpg,.png,.pdf,.zip"
              onChange={onSelectedFile}
              style={{ width: '100% ' }}
              beforeUpload={beforeUpload}
              showUploadList={false}
              disabled={disabled}
            >
              <OutlineButton disabled={disabled} style={{ width: 'fit-content' }}>+ เพิ่มไฟล์</OutlineButton>
            </Upload>
          )}
        </LayoutBodyRow>
      </LayoutBodyColumn>
      <LayoutBackgroundFooter>
        <BackgroundUpload>
          {selectedFile && (
            <ImagePreviewPanel isMobile={isMobile}>
              {selectedFileName ?
                <div>{selectedFileName}</div> :
                <>
                  {isFile ?
                    <div>
                      <a href={preview} target="_blank" rel="noopener noreferrer">
                        Click to download File
                      </a>
                    </div> :
                    <CommentImage src={preview} alt="" />
                  }
                </>
              }
              {isDelete === false && (
                <Image imageName='closeIcon18' width={18} height={18} onClick={onRemoveClick} style={{ cursor: 'pointer' }} />
              )}
            </ImagePreviewPanel>
          )}
        </BackgroundUpload>
      </LayoutBackgroundFooter>
    </Layout>
  )
}
export default UploadFile
